import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  Briefcase, 
  BarChart2,
  Map,
  Calendar,
  Bot,
  FileCheck,
  TrendingUp
} from 'lucide-react';

const PlatformVisualizer = () => {
  const [activeFeature, setActiveFeature] = useState(null);
  const [rotation, setRotation] = useState(0);
  
  // Platform features
  const features = [
    { 
      id: 'autoJM', 
      name: 'AutoJM™', 
      icon: Briefcase, 
      color: '#9333ea', 
      description: 'AI-powered job matching that finds opportunities with 15-20% higher pay'
    },
    { 
      id: 'trInsyte', 
      name: 'TrInsyte™', 
      icon: BarChart2, 
      color: '#c026d3', 
      description: 'Comprehensive analytics platform for contract analysis and pay comparison'
    },
    { 
      id: 'docMgmt', 
      name: 'Document Management', 
      icon: FileText, 
      color: '#4f46e5', 
      description: 'Centralized credential management with expiration alerts and one-click sharing'
    },
    { 
      id: 'shiftMgmt', 
      name: 'Shift Manager', 
      icon: Calendar, 
      color: '#2563eb', 
      description: 'Intelligent schedule tracking and time logging with geolocation verification'
    },
    { 
      id: 'tbot', 
      name: 'T-Bot™', 
      icon: Bot, 
      color: '#3b82f6', 
      description: 'AI assistant for document handling, resume updates, and career guidance'
    },
    { 
      id: 'tripsSystem', 
      name: 'TrIPS™', 
      icon: Map, 
      color: '#0ea5e9', 
      description: 'Location insights and itinerary planning tools help travel healthcare professionals maintain work-life balance'
    },
    { 
      id: 'isoAuction', 
      name: 'ISO-Auction™', 
      icon: TrendingUp, 
      color: '#f59e0b', 
      description: 'Set your terms and let recruiters compete to match your requirements'
    },
    { 
      id: 'liveResume', 
      name: 'Live Resume', 
      icon: FileCheck, 
      color: '#10b981', 
      description: 'Dynamic resume with voice/text updates pulled from your contract history'
    }
  ];

  // Auto rotation effect
  useEffect(() => {
    const timer = setInterval(() => {
      setRotation(prev => (prev + 0.2) % 360);
    }, 50);
    
    return () => clearInterval(timer);
  }, []);

  // Calculate positions for feature nodes in a circular pattern
  const getNodePosition = (index, total) => {
    const angle = (index / total) * 2 * Math.PI;
    const radius = 180; // Distance from center
    
    return {
      x: Math.cos(angle) * radius,
      y: Math.sin(angle) * radius
    };
  };

  return (
    <div className="relative w-full h-96 md:h-[32rem] bg-gray-900 rounded-xl overflow-hidden shadow-lg">
      {/* Platform hub - center sphere */}
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
        <div className="w-24 h-24 rounded-full bg-gradient-to-br from-purple-600 to-purple-900 flex items-center justify-center text-white font-bold text-lg shadow-lg animate-pulse">
          Trvllr™
        </div>
      </div>
      
      {/* Feature nodes */}
      <div 
        className="absolute inset-0 transition-transform duration-300 ease-linear"
        style={{ 
          transform: `rotate(${rotation}deg)`,
          transformOrigin: 'center center' 
        }}
      >
        {features.map((feature, index) => {
          const position = getNodePosition(index, features.length);
          return (
            <div 
              key={feature.id}
              className="absolute top-1/2 left-1/2 transition-transform duration-300 cursor-pointer transform -translate-x-1/2 -translate-y-1/2 hover:scale-110"
              style={{ 
                transform: `translate(calc(-50% + ${position.x}px), calc(-50% + ${position.y}px))` 
              }}
              onClick={() => setActiveFeature(feature)}
              onMouseOver={(e) => e.currentTarget.style.zIndex = 50}
              onMouseOut={(e) => e.currentTarget.style.zIndex = 10}
            >
              {/* Connection line to center */}
              <div 
                className="absolute top-1/2 left-0 h-0.5 origin-left"
                style={{ 
                  width: '100%',
                  background: `linear-gradient(90deg, ${feature.color}00, ${feature.color})`,
                  transform: `rotate(${Math.atan2(0, -position.x) * (180 / Math.PI)}deg)`
                }}
              />
              
              {/* Feature node */}
              <div 
                className="flex items-center justify-center w-16 h-16 rounded-full shadow-lg transform -translate-x-1/2 -translate-y-1/2"
                style={{ 
                  background: `linear-gradient(135deg, ${feature.color}, ${feature.color}cc)`,
                  boxShadow: `0 0 15px ${feature.color}66`
                }}
              >
                <feature.icon className="text-white" size={24} />
              </div>
              
              {/* Feature label */}
              <div className="absolute mt-10 px-2 py-1 bg-gray-800/80 rounded text-white text-xs whitespace-nowrap">
                {feature.name}
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Feature info panel */}
      {activeFeature && (
        <div className="absolute bottom-4 left-4 right-4 bg-gray-800/80 backdrop-blur-sm p-4 rounded-lg border border-purple-500/30 z-50">
          <h3 className="text-xl font-bold text-white mb-2">{activeFeature.name}</h3>
          <p className="text-gray-300">{activeFeature.description}</p>
        </div>
      )}
      
      {/* Instructions */}
      <div className="absolute top-4 left-4 text-xs text-gray-400 z-50">
        Click on a feature to learn more
      </div>
    </div>
  );
};

export default PlatformVisualizer;